// SD50 / FR-AD7: what the operator console's Integrations tab shows per provider, and the
// client-side checks before a save. The server (server/src/ops_integrations.rs `put_integration`)
// is authoritative and re-validates every field; these mirror its rules so a typo is caught at
// the field instead of after the round trip.
import type { IntegrationProvider } from "../api/types";

export interface IntegrationFieldMeta {
  key: string;
  label: string;
  placeholder?: string;
  help?: string;
  required: boolean;
}

export interface IntegrationMeta {
  provider: IntegrationProvider;
  title: string;
  /** One line under the title: what this credential powers. */
  summary: string;
  fields: IntegrationFieldMeta[];
  secretLabel: string;
  /** Short setup checklist for the provider's own console. */
  setup: string[];
}

export const ORCHARD_DEFAULT_BASE_URL = "https://api.orchardrun.com/v1";

export const INTEGRATIONS: IntegrationMeta[] = [
  {
    provider: "google",
    title: "Google",
    summary: "“Continue with Google” sign-in and Google Calendar connect share this OAuth client.",
    fields: [
      {
        key: "client_id",
        label: "Client ID",
        placeholder: "1234567890-abc.apps.googleusercontent.com",
        required: true,
      },
    ],
    secretLabel: "Client secret",
    setup: [
      "Google Cloud console → APIs & Services → Credentials → OAuth client ID (Web application).",
      "Enable the Google Calendar API for the project.",
      "OAuth consent screen scopes: openid, email, profile, calendar.readonly, and calendar.events for notes write-back.",
      "Add both redirect URIs below as Authorized redirect URIs.",
    ],
  },
  {
    provider: "microsoft",
    title: "Microsoft",
    summary: "“Continue with Microsoft” sign-in and Microsoft 365 calendar connect share this Entra app.",
    fields: [
      {
        key: "client_id",
        label: "Application (client) ID",
        placeholder: "00000000-0000-0000-0000-000000000000",
        required: true,
      },
    ],
    secretLabel: "Client secret",
    setup: [
      "Microsoft Entra admin center → App registrations → New registration, accounts in any organizational directory and personal Microsoft accounts.",
      "Authentication → Web platform → add both redirect URIs below.",
      "API permissions (delegated, Microsoft Graph): Calendars.Read, User.Read, offline_access, and Calendars.ReadWrite for notes write-back.",
      "Certificates & secrets → New client secret. Paste the secret Value, not its ID.",
    ],
  },
  {
    provider: "zoom",
    title: "Zoom",
    summary: "The Zoom notetaker reads meeting metadata with this Server-to-Server OAuth app. Without it, Zoom joins run in stub mode.",
    fields: [
      { key: "account_id", label: "Account ID", required: true },
      { key: "client_id", label: "Client ID", required: true },
    ],
    secretLabel: "Client secret",
    setup: [
      "Zoom App Marketplace → Develop → Build app → Server-to-Server OAuth.",
      "Scopes: meeting:read:meeting:admin.",
      "Activate the app, then copy Account ID, Client ID and Client secret here.",
    ],
  },
  {
    provider: "teams",
    title: "Microsoft Teams",
    summary: "The Teams notetaker looks up online meetings through Microsoft Graph with this app. Without it, Teams joins run in stub mode.",
    fields: [
      {
        key: "app_id",
        label: "Application (client) ID",
        placeholder: "00000000-0000-0000-0000-000000000000",
        required: true,
      },
      {
        key: "tenant_id",
        label: "Directory (tenant) ID",
        placeholder: "00000000-0000-0000-0000-000000000000",
        help: "The tenant GUID, or a verified domain like contoso.onmicrosoft.com.",
        required: true,
      },
    ],
    secretLabel: "Client secret",
    setup: [
      "Microsoft Entra admin center → App registrations → New registration (single tenant).",
      "API permissions (application, Microsoft Graph): OnlineMeetings.Read.All, then Grant admin consent.",
      "Certificates & secrets → New client secret. Paste the secret Value, not its ID.",
    ],
  },
  {
    provider: "hosted_stt",
    title: "Hosted speech-to-text",
    summary:
      "Orchardrun transcription and speaker labels, used when an org has no speech-to-text account of its own — post-meeting transcripts and live meeting transcripts. Never used for language models.",
    fields: [
      {
        key: "base_url",
        label: "API base URL",
        placeholder: ORCHARD_DEFAULT_BASE_URL,
        help: `Optional. Leave blank for ${ORCHARD_DEFAULT_BASE_URL}.`,
        required: false,
      },
    ],
    secretLabel: "API key",
    setup: [
      "Create an API key in the Orchardrun dashboard for this environment (separate keys for staging and production).",
      "Orgs still have to turn transcription on, and an org's own account always wins over hosted.",
    ],
  },
  {
    // SD55: not a provider app credential — the dedicated Meet notetaker's saved Google web
    // session. Google walls scripted logins, so the operator captures it once by hand and pastes
    // the resulting JSON here; the bot replays it to join Workspace meetings that wall guests.
    provider: "meet_bot_session",
    title: "Meet notetaker session",
    summary:
      "The signed-in Google session the Meet notetaker joins Workspace-gated meetings with. Without it, Meet joins are guest-only and fail meetings that require a signed-in account.",
    fields: [
      {
        key: "account_email",
        label: "Bot account email",
        placeholder: "notetaker@your-domain.com",
        help: "Which dedicated Google account this session belongs to. For your reference only.",
        required: false,
      },
    ],
    secretLabel: "Session JSON",
    setup: [
      "Use a DEDICATED Google account for the bot (never a person's) — name it so it reads as a bot, e.g. “Gloom Notetaker”.",
      "On a machine with a display, run: node bot/scripts/google-login.js ./bot-session.json",
      "Sign that account in in the window that opens, finish any 2FA, then let it save the file.",
      "Paste the contents of bot-session.json below. Re-paste to rotate when Google expires the session.",
    ],
  },
];

export function integrationMeta(provider: IntegrationProvider): IntegrationMeta {
  const meta = INTEGRATIONS.find((m) => m.provider === provider);
  if (!meta) throw new Error(`unknown integration ${provider}`);
  return meta;
}

export interface IntegrationFormValues {
  enabled: boolean;
  fields: Record<string, string>;
  secret: string;
  clearSecret: boolean;
  /** Whether a secret is already stored (the form cannot see it). */
  secretSet: boolean;
}

/** Keyed by field key, plus `secret` for the secret input. */
export type IntegrationFormErrors = Record<string, string>;

const GUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

function fieldError(provider: IntegrationProvider, key: string, value: string): string | null {
  if (value.length > 512) return "Too long.";
  if (key !== "base_url" && /\s/.test(value)) return "Must not contain spaces.";
  if (provider === "google" && key === "client_id" && !value.endsWith(".apps.googleusercontent.com")) {
    return "A Google client ID ends in .apps.googleusercontent.com.";
  }
  if ((provider === "microsoft" && key === "client_id") || (provider === "teams" && key === "app_id")) {
    if (!GUID_RE.test(value)) return "Use the application (client) ID GUID from the app's Overview page.";
  }
  if (provider === "teams" && key === "tenant_id" && !(GUID_RE.test(value) || value.includes("."))) {
    return "Use the directory (tenant) ID GUID or a verified domain.";
  }
  if (key === "base_url") {
    let url: URL;
    try {
      url = new URL(value);
    } catch {
      return "Enter a full URL like https://api.orchardrun.com/v1.";
    }
    if (url.protocol !== "https:") return "Must use https.";
    if (url.search || url.hash) return "Remove the query string.";
  }
  return null;
}

export function validateIntegration(provider: IntegrationProvider, v: IntegrationFormValues): IntegrationFormErrors {
  const errors: IntegrationFormErrors = {};
  for (const f of integrationMeta(provider).fields) {
    const value = (v.fields[f.key] ?? "").trim();
    if (!value) {
      if (f.required && v.enabled) errors[f.key] = `${f.label} is required.`;
      continue;
    }
    const err = fieldError(provider, f.key, value);
    if (err) errors[f.key] = err;
  }
  const secret = v.secret.trim();
  if (provider === "meet_bot_session") {
    // SD55: the session is a large JSON blob, not a single-token secret — mirror the server:
    // bigger cap, no no-spaces rule, and a storageState shape check instead.
    if (secret && secret.length > 256 * 1024) errors.secret = "Too long.";
    else if (secret) {
      try {
        const parsed = JSON.parse(secret) as { cookies?: unknown; origins?: unknown };
        if (!Array.isArray(parsed.cookies) || !Array.isArray(parsed.origins)) {
          errors.secret = "Expected the JSON from the login helper (with cookies and origins).";
        }
      } catch {
        errors.secret = "Paste the JSON produced by bot/scripts/google-login.js.";
      }
    }
  } else if (secret && /\s/.test(secret)) errors.secret = "Must not contain spaces.";
  else if (secret.length > 4096) errors.secret = "Too long.";
  const willHaveSecret = secret !== "" || (v.secretSet && !v.clearSecret);
  if (v.enabled && !willHaveSecret) {
    errors.secret = v.clearSecret
      ? "Turn the integration off to clear its secret, or enter a new one."
      : `${integrationMeta(provider).secretLabel} is required.`;
  }
  return errors;
}
